import { useCallback } from 'react';
import { message } from 'antd';
import { getUserDetails, makePostCall } from '../../../utils/helper';
import { copyDocument, createDocument, forceSaveDocument } from '../../OnlyOfficeDocuments/documentsApi';
import { STATUS_MAP } from '../constants';
import type { OrderDetails, OoReport } from '../types';

interface UseReportActionsOodocsOptions {
  orderDetails: OrderDetails | null;
  currentReport: OoReport | null;
  /** Fallback doc ID when currentReport has no linked document (e.g. first draft before any report row exists) */
  activeDocId: number | null;
  /** True when the editor is showing a document for READING only (a historical entry on a
   *  signed order). It matters here because it decides which document a submit targets —
   *  see the resolution rule in saveReport. */
  docViewOnly?: boolean;
  isSignedOff: boolean;
  /**
   * Fired after a successful commit. `previewDocId` is the document to reopen read-only for
   * the post-commit preview — on sign-off the backend mints a NEW signed doc, so this is the
   * response's `document_id` (falls back to the submitted doc for in-place promotes).
   */
  onSaved?: (status: string, previewDocId: number, newDocKey?: string) => void;
}

/**
 * OnlyOffice variant of useReportActions.
 *
 * Key differences from the TinyMCE version:
 * - No HTML content extraction — OnlyOffice autosaves to server
 * - Submits to /submit-report-oodocs with document_id instead of HTML
 * - Copy prior reports via /documents/copy
 * - Backend handles header update on signoff
 */
export const useReportActionsOodocs = ({
  orderDetails,
  currentReport,
  activeDocId,
  docViewOnly = false,
  isSignedOff,
  onSaved,
}: UseReportActionsOodocsOptions) => {
  const pacs_order = orderDetails?.pacs_order;
  const patient = pacs_order?.patient;

  /**
   * Submit report status (draft / signoff).
   * No editor content needed — the .docx is already saved on the server via OnlyOffice callback.
   */
  const saveReport = useCallback(async (
    statusCode: string,
    extraPayload: Record<string, any> = {},
  ) => {
    // Submit the document actually being EDITED. Usually that is currentReport's document,
    // but on a signed order the editing surface is a scratch fork while currentReport stays
    // pinned to the signed report as the action context — submitting currentReport's doc
    // there would snapshot the old signed file and ignore everything just dictated.
    //
    // docViewOnly is what separates the two cases: a read-only historical entry also moves
    // activeDocId, and Sign Off must not target whatever the user happens to be reading.
    const documentId = (!docViewOnly && activeDocId)
      ? activeDocId
      : (currentReport?.document?.doc_id || activeDocId);
    if (!pacs_order || !patient || !documentId) {
      message.error('No document to save');
      return;
    }

    const backendStatus = STATUS_MAP[statusCode] || statusCode;

    try {
      const res = await makePostCall('/submit-report-oodocs', {
        order_id: pacs_order.pacs_ord_id,
        document_id: documentId,
        yh_no: patient.pat_pin,
        user_id: getUserDetails()?.username,
        status: backendStatus,
        report_id: currentReport?.pr_id,
        is_editing_signedoff: isSignedOff,
        ...extraPayload,
      });

      const newDocKey = res.data?.data?.doc_key;
      // Sign-off mints a new signed doc (own id + freshly-stamped key); in-place draft/
      // reviewed promotes reuse the submitted doc. Reopen whichever the backend points at.
      const previewDocId = res.data?.data?.document_id ?? documentId;
      // Report the FRONTEND status code ('SIGNEDOFF', 'DRAFTED', ...) — consumers compare
      // against these, not the lowercase backend API values.
      onSaved?.(statusCode, previewDocId, newDocKey);
    } catch (err: any) {
      console.error('Failed to save report:', err);
      message.error(err.response?.data?.message || 'Failed to save report');
    }
  }, [pacs_order, patient, currentReport, activeDocId, docViewOnly, isSignedOff, onSaved]);

  /**
   * Delete a report. Returns true on success (backend enforces creator-only + signed lock).
   */
  const deleteReport = useCallback(async (report: OoReport): Promise<boolean> => {
    if (!report.pr_id) return false;
    try {
      await makePostCall('/delete-report-oodocs', {
        report_id: report.pr_id,
        order_id: report.pr_pacs_ord_id,
        user_id: getUserDetails()?.username,
      });
      message.success('Report deleted successfully');
      return true;
    } catch (err: any) {
      console.error('Failed to delete report:', err);
      message.error(err.response?.data?.message || 'Failed to delete report');
      return false;
    }
  }, [patient]);

  /**
   * Seed a private AUTOSAVED working copy from a committed (DRAFTED/REVIEWED) document of
   * THIS order. Editing a committed report directly would autosave into the shared document
   * everyone sees; instead the user edits this private copy, which is later promoted to a new
   * committed entry (Draft / Reviewed / Sign off). Backed by /documents/copy, which duplicates
   * the source content and creates the private AUTOSAVED row.
   * Returns the new document ID, or null on failure.
   */
  const createWorkingCopy = useCallback(async (sourceDocId: number): Promise<number | null> => {
    if (!pacs_order || !sourceDocId) return null;
    try {
      const res = await copyDocument({
        sourceDocumentId: sourceDocId,
        pacsOrdId: pacs_order.pacs_ord_id,
        title: `Working copy - ${new Date().toLocaleString()}`,
      });
      return res.data?.data?.id || res.data?.data?.doc_id || null;
    } catch (err: any) {
      console.error('Failed to create working copy:', err);
      message.error(err.response?.data?.message || 'Failed to open report for editing');
      return null;
    }
  }, [pacs_order]);

  /**
   * Create a new blank document for the current order. The backend links it to a
   * private AUTOSAVED report row (visible only to this user), so the doc survives a
   * reload while the order stays SCANNED until the user explicitly clicks Draft.
   * Returns the new document ID.
   */
  const createNewDocument = useCallback(async (): Promise<number | null> => {
    if (!pacs_order || !patient) return null;

    try {
      const res = await createDocument({
        title: `Report - ${new Date().toLocaleString()}`,
        pacsOrdId: pacs_order.pacs_ord_id,
      });
      return res.data?.data?.id || null;
    } catch (err: any) {
      console.error('Failed to create document:', err);
      message.error(err.response?.data?.message || 'Failed to create document');
      return null;
    }
  }, [pacs_order, patient]);

  /**
   * Fork the current document as a new DRAFTED entry (the old app's repeat-Draft click):
   * 1. Force-flush OO latest content to storage
   * 2. Copy the document to a new one under the same order
   * 3. Promote the copy's report row to DRAFTED (no report_id → backend resolves by doc)
   * The caller switches the editor to the copy, which then receives the autosaves.
   * Returns the new document ID, or null on failure.
   */
  const saveAsDraft = useCallback(async (proxyUser?: string): Promise<number | null> => {
    if (!pacs_order || !patient) return null;

    const sourceDocId = currentReport?.document?.doc_id || activeDocId;
    if (!sourceDocId) {
      message.error('No document to copy');
      return null;
    }

    try {
      await forceSaveDocument(sourceDocId);

      const copyRes = await copyDocument({
        sourceDocumentId: sourceDocId,
        pacsOrdId: pacs_order.pacs_ord_id,
        title: `Draft - ${new Date().toLocaleString()}`,
      });
      const newDocId = copyRes.data?.data?.id || copyRes.data?.data?.doc_id;
      if (!newDocId) throw new Error('Failed to get new document ID from copy');

      await makePostCall('/submit-report-oodocs', {
        order_id: pacs_order.pacs_ord_id,
        document_id: newDocId,
        yh_no: patient.pat_pin,
        user_id: getUserDetails()?.username,
        status: STATUS_MAP['DRAFTED'] || 'draft',
        // Intentionally no report_id → backend resolves the copy's own report row by doc_id
        ...(proxyUser ? { proxy_user: proxyUser } : {}),
      });

      message.success('Saved as new draft');
      return newDocId;
    } catch (err: any) {
      console.error('Failed to save as draft:', err);
      message.error(err.response?.data?.message || 'Failed to save as draft');
      return null;
    }
  }, [pacs_order, patient, currentReport, activeDocId]);

  /**
   * Mark the current content as REVIEWED — a fork parallel to saveAsDraft:
   * 1. Force-flush OO latest content to storage
   * 2. Copy the document to a new one under the same order
   * 3. Promote the copy's report row to REVIEWED. The order status advances to
   *    REVIEWED (Draft is locked from here on — the workflow never regresses).
   * The caller switches the editor to the copy, which then receives the autosaves.
   * Returns the new document ID, or null on failure.
   */
  const saveAsReviewed = useCallback(async (proxyUser?: string): Promise<number | null> => {
    if (!pacs_order || !patient) return null;

    const sourceDocId = currentReport?.document?.doc_id || activeDocId;
    if (!sourceDocId) {
      message.error('No document to review');
      return null;
    }

    try {
      await forceSaveDocument(sourceDocId);

      const copyRes = await copyDocument({
        sourceDocumentId: sourceDocId,
        pacsOrdId: pacs_order.pacs_ord_id,
        title: `Reviewed - ${new Date().toLocaleString()}`,
      });
      const newDocId = copyRes.data?.data?.id || copyRes.data?.data?.doc_id;
      if (!newDocId) throw new Error('Failed to get new document ID from copy');

      await makePostCall('/submit-report-oodocs', {
        order_id: pacs_order.pacs_ord_id,
        document_id: newDocId,
        yh_no: patient.pat_pin,
        user_id: getUserDetails()?.username,
        status: STATUS_MAP['REVIEWED'] || 'reviewed',
        // Intentionally no report_id → backend resolves the copy's own report row by doc_id
        ...(proxyUser ? { proxy_user: proxyUser } : {}),
      });

      message.success('Marked as reviewed');
      return newDocId;
    } catch (err: any) {
      console.error('Failed to mark as reviewed:', err);
      message.error(err.response?.data?.message || 'Failed to mark as reviewed');
      return null;
    }
  }, [pacs_order, patient, currentReport, activeDocId]);

  return { saveReport, deleteReport, createWorkingCopy, createNewDocument, saveAsDraft, saveAsReviewed };
};
